import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Building2, Gauge, Wrench, Wallet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/Logo";

const SERVICE_OPTIONS = [
  "Visita técnica", "Manutenção preventiva", "Manutenção corretiva", "Instalação",
  "Limpeza / higienização", "Carga de refrigerante", "Câmaras frias", "Chillers",
];

const STEPS = [
  { id: "empresa", title: "Sua empresa", icon: Building2 },
  { id: "precos", title: "Seus preços base", icon: Gauge },
  { id: "servicos", title: "Serviços realizados", icon: Wrench },
  { id: "custos", title: "Custos operacionais", icon: Wallet },
];

export default function Onboarding() {
  const { refresh, company } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: company.name || "", owner_name: company.owner_name || "", phone: "", whatsapp: "",
    email: "", city: "", state: "", doc: "", address: "",
    settings: {
      valor_hora: 120, valor_km: 2.5, impostos_pct: 6, margem_pct: 30,
      deslocamento_minimo: 50, num_tecnicos: 1, servicos_realizados: [],
    },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, settings: { ...form.settings, [k]: Number(e.target.value) || 0 } });

  const toggleService = (s) => {
    const list = form.settings.servicos_realizados;
    setForm({
      ...form,
      settings: { ...form.settings, servicos_realizados: list.includes(s) ? list.filter((x) => x !== s) : [...list, s] },
    });
  };

  const finish = async (dest = "/app/calculadora") => {
    setSaving(true);
    try {
      await api.put("/company", { ...form, onboarding_done: true });
      await refresh();
      toast.success(dest === "/app/custos" ? "Configuração concluída! Agora vamos aos seus custos." : "Configuração concluída! Vamos ao seu primeiro orçamento.");
      navigate(dest);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-6 animate-in-up">
      <div className="flex justify-center mb-6"><Logo /></div>
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-sky-600" : "bg-slate-200"}`} />
            <p className={`mt-2 text-xs font-medium ${i <= step ? "text-sky-700" : "text-slate-400"}`}>{s.title}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 sm:p-6">
        {step === 0 && (
          <div className="space-y-4" data-testid="onboarding-step-empresa">
            <h2 className="text-xl font-bold">Dados da empresa</h2>
            <p className="text-sm text-slate-500">Esses dados aparecem nos seus orçamentos em PDF.</p>
            <div className="space-y-1.5">
              <Label>Nome da empresa *</Label>
              <Input className="h-12" data-testid="ob-company-name" value={form.name} onChange={set("name")} placeholder="Silva Refrigeração" />
            </div>
            <div className="space-y-1.5">
              <Label>Responsável</Label>
              <Input className="h-12" data-testid="ob-owner-name" value={form.owner_name} onChange={set("owner_name")} placeholder="Seu nome" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Telefone</Label>
                <Input className="h-12" data-testid="ob-phone" value={form.phone} onChange={set("phone")} placeholder="(11) 4002-8922" />
              </div>
              <div className="space-y-1.5">
                <Label>WhatsApp</Label>
                <Input className="h-12" data-testid="ob-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="(11) 99999-0000" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>E-mail comercial</Label>
              <Input className="h-12" data-testid="ob-email" type="email" value={form.email} onChange={set("email")} placeholder="contato@empresa.com.br" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Cidade</Label>
                <Input className="h-12" data-testid="ob-city" value={form.city} onChange={set("city")} placeholder="São Paulo" />
              </div>
              <div className="space-y-1.5">
                <Label>UF</Label>
                <Input className="h-12" data-testid="ob-state" maxLength={2} value={form.state} onChange={set("state")} placeholder="SP" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>CNPJ/CPF (opcional)</Label>
              <Input className="h-12" data-testid="ob-doc" value={form.doc} onChange={set("doc")} placeholder="00.000.000/0001-00" />
            </div>
            <div className="space-y-1.5">
              <Label>Endereço</Label>
              <Input className="h-12" data-testid="ob-address" value={form.address} onChange={set("address")} placeholder="Rua, número, bairro" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="onboarding-step-precos">
            <h2 className="text-xl font-bold">Parâmetros de precificação</h2>
            <p className="text-sm text-slate-500">Eles alimentam o motor de cálculo. Você pode mudar depois em Configurações.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Hora técnica (R$)</Label>
                <Input className="h-12 font-mono-num" data-testid="ob-valor-hora" type="number" step="0.01" value={form.settings.valor_hora} onChange={setNum("valor_hora")} />
              </div>
              <div className="space-y-1.5">
                <Label>Valor por km (R$)</Label>
                <Input className="h-12 font-mono-num" data-testid="ob-valor-km" type="number" step="0.01" value={form.settings.valor_km} onChange={setNum("valor_km")} />
              </div>
              <div className="space-y-1.5">
                <Label>Impostos (%)</Label>
                <Input className="h-12 font-mono-num" data-testid="ob-impostos" type="number" step="0.1" value={form.settings.impostos_pct} onChange={setNum("impostos_pct")} />
              </div>
              <div className="space-y-1.5">
                <Label>Margem desejada (%)</Label>
                <Input className="h-12 font-mono-num" data-testid="ob-margem" type="number" step="1" value={form.settings.margem_pct} onChange={setNum("margem_pct")} />
              </div>
              <div className="space-y-1.5">
                <Label>Deslocamento mínimo (R$)</Label>
                <Input className="h-12 font-mono-num" data-testid="ob-desl-min" type="number" step="0.01" value={form.settings.deslocamento_minimo} onChange={setNum("deslocamento_minimo")} />
              </div>
              <div className="space-y-1.5">
                <Label>Nº de técnicos</Label>
                <Input className="h-12 font-mono-num" data-testid="ob-tecnicos" type="number" min="1" value={form.settings.num_tecnicos} onChange={setNum("num_tecnicos")} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-step-servicos">
            <h2 className="text-xl font-bold">Quais serviços você realiza?</h2>
            <p className="text-sm text-slate-500">Selecione os principais. O catálogo completo fica disponível no app.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SERVICE_OPTIONS.map((s) => (
                <label key={s} className={`flex items-center gap-3 border rounded-lg p-3.5 cursor-pointer transition-colors ${form.settings.servicos_realizados.includes(s) ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}
                       data-testid={`ob-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Checkbox checked={form.settings.servicos_realizados.includes(s)} onCheckedChange={() => toggleService(s)} />
                  <span className="text-sm font-medium">{s}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="onboarding-step-custos">
            <h2 className="text-xl font-bold">Seus custos operacionais</h2>
            <p className="text-sm text-slate-600">O GEPR não pergunta só "quanto os outros cobram": ele calcula quanto custa para a SUA empresa operar — e combina isso com as referências de mercado para sugerir o seu preço.</p>
            <p className="text-sm text-slate-600">Leva menos de 2 minutos: são 7 perguntas essenciais e, se não souber alguma resposta, toque em «Não sei» que sugerimos uma referência do mercado brasileiro.</p>
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900">
              Sem essa configuração mínima, o cadastro de clientes fica bloqueado — é ela que permite ao GEPR precificar pela sua realidade, e não por uma tabela genérica.
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {step > 0 && (
            <Button variant="outline" className="h-12" onClick={() => setStep(step - 1)} data-testid="ob-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
            </Button>
          )}
          {step < 3 && (
            <Button className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={() => {
                      if (step === 0 && !form.name.trim()) {
                        toast.error("Informe o nome da empresa.");
                        return;
                      }
                      setStep(step + 1);
                    }}
                    data-testid="ob-next-btn">
              Continuar <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {step === 3 && (
            <div className="flex-1 space-y-2">
              <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => finish("/app/custos")} disabled={saving} data-testid="ob-finish-costs-btn">
                {saving ? "Salvando..." : "Configurar meus custos agora (2 min)"}
              </Button>
              <Button variant="ghost" className="w-full h-10 text-slate-500" onClick={() => finish()} disabled={saving} data-testid="ob-finish-later-btn">
                Fazer depois — ir ao primeiro orçamento
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
